import { createTheme } from "@mui/material";
import colors from "./Colors";
import { tokens } from "./Tokens";
import { muiRadioStyle } from "./radioStyle";
import { muiSwitchStyle } from "./switchStyle";
import { typography } from "./typography";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    label: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    alert: true;
    primary: true;
    secondary: true;
    secondaryWhite: true;
    primaryWhite: true;
  }
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 375,
      sm: 768,
      md: 1024,
      lg: 1440,
      xl: 1980
    }
  },
  palette: {
    background: {
      default: tokens.Background.General.Secondary
    },
    text: {
      primary: "#64748B"
    },
    primary: {
      main: colors.primaryColor,
      contrastText: tokens.Text.General.Inverse
    },
    secondary: {
      main: colors.secondaryColor,
      contrastText: colors.primaryColor
    },
    warning: {
      main: tokens.Text.Feedback.Alert
    }
  },

  typography,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "600",
          borderRadius: "8px",
          textTransform: "none"
        }
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: colors.primaryColor,
            color: tokens.Background.General.Primary,
            border: `1px solid ${colors.primaryColor}`,
            "&:hover": {
              color: tokens.Text.General.Color,
              "& .count-badge": {
                color: tokens.Text.General.Inverse
              },
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              border: `2px solid ${colors.primaryColor}`,
              color: tokens.Border.General.Color,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Background.General.Primary,
              backgroundColor: colors.primaryColor
            }
          }
        },
        {
          props: { variant: "primaryWhite" },
          style: {
            color: tokens.Text.General.Color,
            backgroundColor: tokens.Background.General.Primary,
            ".icon-svg": {
              stroke: colors.primaryColor
            },
            "&:hover": {
              border: `1px solid ${tokens.Border.General.Inverse}`,
              backgroundColor: "transparent",
              color: tokens.Background.General.Primary,
              ".icon-svg": {
                stroke: tokens.Icon.General.Inverse
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              border: `2px solid ${tokens.Border.General.Inverse}`,
              color: tokens.Background.General.Primary,
              ".icon-svg": {
                stroke: tokens.Icon.General.Inverse
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Border.General.Color
            }
          }
        },
        {
          props: { variant: "secondary" },
          style: {
            border: `1px solid ${colors.primaryColor}`,
            color: tokens.Text.General.Color,
            "&:hover": {
              backgroundColor: colors.primaryColor,
              color: tokens.Background.General.Primary,
              ".icon-svg": {
                stroke: tokens.Icon.General.Inverse
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              backgroundColor: colors.primaryColor,
              color: tokens.Background.General.Primary,
              ".icon-svg": {
                stroke: tokens.Icon.General.Inverse
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Text.General.Color
            }
          }
        },
        {
          props: { variant: "secondaryWhite" },
          style: {
            color: tokens.Background.General.Primary,
            border: `1px solid ${tokens.Background.General.Primary}`,
            ".icon-svg": {
              stroke: tokens.Background.General.Primary
            },
            "&:hover": {
              border: `1px solid ${tokens.Background.General.Primary}`,
              backgroundColor: tokens.Background.General.Primary,
              color: colors.primaryColor,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.Inverse}`
            },
            "&:active": {
              border: `1px solid ${tokens.Border.General.LightColor}`,
              backgroundColor: tokens.Background.General.Primary,
              color: colors.primaryColor,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Background.General.Primary
            }
          }
        },
        {
          props: { variant: "alert" },
          style: {
            backgroundColor: tokens.Background.Feedback.Alert,
            color: tokens.Border.Feedback.Alert,
            ".icon-svg": {
              stroke: tokens.Border.Feedback.Alert
            },
            "&:hover": {
              backgroundColor: tokens.Background.General.Primary,
              border: `1px solid ${tokens.Border.Feedback.Alert}`
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.Feedback.LightAlert}`
            },
            "&:active": {
              backgroundColor: tokens.Background.General.Primary,
              border: `1px solid ${tokens.Border.Feedback.Alert}`
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Border.Feedback.Alert
            }
          }
        },
        {
          props: { variant: "text" },
          style: {
            margin: "15px 0",
            padding: "0",
            border: "0"
          }
        }
      ]
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          height: "34px",
          width: "34px",
          padding: "8px",
          color: tokens.Icon.General.Secondary,
          "&:hover": {
            background: tokens.Icon.General.Inverse,
            color: tokens.Icon.General.Secondary
          },
          "&:active": {
            border: `2px solid ${tokens.Border.General.Secondary}`,
            color: tokens.Icon.General.Secondary
          },
          "&:focus": {
            border: `2px solid ${tokens.Border.General.Secondary}`,
            background: tokens.Icon.General.Inverse,
            color: tokens.Icon.General.Secondary
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            "& > .MuiGrid-root": {
              overflow: "hidden"
            }
          },
          "& #actionPlanDialog": {
            padding: "0",
            "& .MuiTabs-root": {
              backgroundColor: tokens.Background.General.Color,
              padding: "0px 24px"
            },
            "& .MuiTabPanel-root": {
              padding: "0px 24px"
            },
            "& > .MuiBox-root": {
              height: "100%",
              backgroundColor: "unset",
              "& div[role='tabpanel']": {
                backgroundColor: tokens.Background.General.Primary,
                overflow: "auto",
                "& > .MuiGrid-root": {
                  paddingBottom: 0
                }
              }
            },
            "&.all_plans": {
              ".MuiTab-content": {
                overflowY: "auto"
              }
            },
            "&.my_plans.isNew": {
              overflowY: "auto",
              height: "400px",
              padding: "16px"
            },
            "&.my_plans": {
              ".MuiTab-content": {
                overflowY: "auto",
                height: "400px",
                marginTop: "0"
              }
            }
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "93%"
        },
        colorPrimary: {
          backgroundColor: tokens.Background.General.Secondary,
          color: colors.primaryColor
        }
      }
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "22px",
          color: tokens.Text.General.Tertiary,
          width: "fit-content",
          minWidth: "auto",
          padding: "0",
          "&.Mui-selected": {
            opacity: 1,
            "&:hover": {
              opacity: 1
            }
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          gap: "32px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "#F7F8F9"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: "none",
          backgroundColor: tokens.Background.General.Color,
          color: tokens.Text.General.Color
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.Text.General.PrimaryHeadline,
          color: tokens.Background.General.Primary,
          padding: "8px 16px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          "& .MuiTooltip-arrow": {
            color: tokens.Text.General.PrimaryHeadline
          }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: "2px",
          borderRadius: "100px",
          backgroundColor: tokens.Background.General.Tertiary
        },
        barColorPrimary: {
          backgroundColor: tokens.Text.Feedback.ProgressLight
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: tokens.Primary.Primary400,
          "&.Mui-active": {
            color: tokens.Primary.Primary400
          },
          "&.Mui-completed": {
            color: tokens.Primary.Primary400
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            minHeight: "43px",
            boxSizing: "border-box",
            borderColor: tokens.Border.General.Tertiary,
            "& #outlined-basic": {
              padding: "4px 0 4px 4px"
            },

            "&:hover ": {
              borderColor: tokens.Border.General.Primary
            },
            "&:focus ": {
              borderColor: tokens.Border.General.LightColor
            }
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: tokens.Background.General.Color,
            "& p, & span, & a, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: tokens.Text.General.Color
            }
          },
          "&.Mui-selected": {
            backgroundColor: tokens.Background.General.Color,
            "& p, & span, & a, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: tokens.Text.General.Color
            }
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          "&:hover": {
            backgroundColor: tokens.Background.General.Color,
            "& p, & span, & a, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: tokens.Text.General.Color
            }
          },
          "&:focus": {
            backgroundColor: tokens.Background.General.Color,
            "& p, & span, & a, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: tokens.Text.General.Color
            }
          }
        }
      }
    },
    ...muiSwitchStyle.components,
    ...muiRadioStyle.components
  }
});
